import { useState } from 'react';
import Layout from '../../../components/feature/Layout';

interface Incident {
  id: string;
  clientName: string;
  operationId: string;
  type: string;
  priority: 'critical' | 'high' | 'medium' | 'low';
  description: string;
  assignedTo: string;
  status: 'open' | 'in_progress' | 'resolved';
  createdDate: string;
  resolvedDate?: string;
}

export default function ClientIncidentsPage() {
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedIncident, setSelectedIncident] = useState<Incident | null>(null);
  const [filterType, setFilterType] = useState('all');
  const [filterPriority, setFilterPriority] = useState('all');

  const incidents: Incident[] = [
    {
      id: 'INC-001',
      clientName: 'Importadora Global S.A.',
      operationId: 'OP-2024-0145',
      type: 'Retraso en Entrega',
      priority: 'high',
      description: 'Cliente reporta retraso de 3 días en la entrega de mercancía',
      assignedTo: 'Carlos Méndez',
      status: 'in_progress',
      createdDate: '2024-01-15'
    },
    {
      id: 'INC-002',
      clientName: 'Exportadora del Pacífico',
      operationId: 'OP-2024-0132',
      type: 'Documentación Incorrecta',
      priority: 'critical',
      description: 'Error en factura comercial, requiere corrección urgente',
      assignedTo: 'Ana García',
      status: 'open',
      createdDate: '2024-01-16'
    },
    {
      id: 'INC-003',
      clientName: 'Logística Continental',
      operationId: 'OP-2024-0128',
      type: 'Daño en Mercancía',
      priority: 'high',
      description: 'Cliente reporta daño en 5 cajas durante el transporte',
      assignedTo: 'Roberto Silva',
      status: 'resolved',
      createdDate: '2024-01-10',
      resolvedDate: '2024-01-14'
    },
    {
      id: 'INC-004',
      clientName: 'Comercial Centroamericana',
      operationId: 'OP-2024-0115',
      type: 'Cobro Incorrecto',
      priority: 'medium',
      description: 'Cliente solicita revisión de factura por cobro duplicado',
      assignedTo: 'María López',
      status: 'in_progress',
      createdDate: '2024-01-12'
    },
    {
      id: 'INC-005',
      clientName: 'Distribuidora Nacional',
      operationId: 'OP-2024-0098',
      type: 'Falta de Comunicación',
      priority: 'low',
      description: 'Cliente solicita mayor frecuencia en actualizaciones de estado',
      assignedTo: 'Carlos Méndez',
      status: 'resolved',
      createdDate: '2024-01-08',
      resolvedDate: '2024-01-09'
    },
    {
      id: 'INC-006',
      clientName: 'Importadora Global S.A.',
      operationId: 'OP-2024-0089',
      type: 'Retraso en Despacho',
      priority: 'critical',
      description: 'Mercancía detenida en aduana por documentación faltante',
      assignedTo: 'Ana García',
      status: 'open',
      createdDate: '2024-01-17'
    }
  ];

  const getPriorityBadge = (priority: string) => {
    const badges = {
      critical: 'bg-red-100 text-red-800',
      high: 'bg-orange-100 text-orange-800',
      medium: 'bg-yellow-100 text-yellow-800',
      low: 'bg-blue-100 text-blue-800'
    };
    const labels = {
      critical: 'Crítica',
      high: 'Alta',
      medium: 'Media',
      low: 'Baja'
    };
    return { class: badges[priority as keyof typeof badges], label: labels[priority as keyof typeof labels] };
  };

  const getStatusBadge = (status: string) => {
    const badges = {
      open: 'bg-red-100 text-red-800',
      in_progress: 'bg-yellow-100 text-yellow-800',
      resolved: 'bg-green-100 text-green-800'
    };
    const labels = {
      open: 'Abierta',
      in_progress: 'En Proceso',
      resolved: 'Resuelta'
    };
    return { class: badges[status as keyof typeof badges], label: labels[status as keyof typeof labels] };
  };

  const filteredIncidents = incidents.filter(incident => {
    if (filterType !== 'all' && incident.type !== filterType) return false;
    if (filterPriority !== 'all' && incident.priority !== filterPriority) return false;
    return true;
  });

  return (
    <Layout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-[#0A2A43]">Incidencias de Cliente</h1>
            <p className="text-sm text-gray-500 mt-1">Registro y seguimiento de reclamos y quejas</p>
          </div>
          <button
            onClick={() => setShowCreateModal(true)}
            className="px-4 py-2.5 bg-gradient-to-r from-[#114B7A] to-[#2F80ED] text-white rounded-lg hover:shadow-lg transition-all flex items-center gap-2 whitespace-nowrap cursor-pointer"
          >
            <i className="ri-add-line text-lg"></i>
            Nueva Incidencia
          </button>
        </div>

        {/* KPIs */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Total Incidencias</p>
                <p className="text-2xl font-bold text-gray-900 mt-1">{incidents.length}</p>
              </div>
              <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                <i className="ri-alert-line text-2xl text-blue-600"></i>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Abiertas</p>
                <p className="text-2xl font-bold text-red-600 mt-1">
                  {incidents.filter(i => i.status === 'open').length}
                </p>
              </div>
              <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center">
                <i className="ri-error-warning-line text-2xl text-red-600"></i>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">En Proceso</p>
                <p className="text-2xl font-bold text-yellow-600 mt-1">
                  {incidents.filter(i => i.status === 'in_progress').length}
                </p>
              </div>
              <div className="w-12 h-12 bg-yellow-100 rounded-lg flex items-center justify-center">
                <i className="ri-time-line text-2xl text-yellow-600"></i>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Resueltas</p>
                <p className="text-2xl font-bold text-green-600 mt-1">
                  {incidents.filter(i => i.status === 'resolved').length}
                </p>
              </div>
              <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                <i className="ri-checkbox-circle-line text-2xl text-green-600"></i>
              </div>
            </div>
          </div>
        </div>

        {/* Filtros */}
        <div className="bg-white rounded-lg shadow-[0_4px_16px_rgba(10,42,67,0.08)] p-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Tipo de Incidencia</label>
              <select
                value={filterType}
                onChange={(e) => setFilterType(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="all">Todos los tipos</option>
                <option value="Retraso en Entrega">Retraso en Entrega</option>
                <option value="Documentación Incorrecta">Documentación Incorrecta</option>
                <option value="Daño en Mercancía">Daño en Mercancía</option>
                <option value="Cobro Incorrecto">Cobro Incorrecto</option>
                <option value="Falta de Comunicación">Falta de Comunicación</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Prioridad</label>
              <select
                value={filterPriority}
                onChange={(e) => setFilterPriority(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="all">Todas las prioridades</option>
                <option value="critical">Crítica</option>
                <option value="high">Alta</option>
                <option value="medium">Media</option>
                <option value="low">Baja</option>
              </select>
            </div>
          </div>
        </div>

        {/* Tabla de Incidencias */}
        <div className="bg-white rounded-lg shadow-[0_4px_16px_rgba(10,42,67,0.08)]">
          <div className="p-6 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900">Incidencias Registradas</h2>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">ID</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Cliente</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Operación</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Tipo</th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase">Prioridad</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Responsable</th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase">Estado</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Fecha</th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase">Acciones</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {filteredIncidents.map((incident) => {
                  const priorityBadge = getPriorityBadge(incident.priority);
                  const statusBadge = getStatusBadge(incident.status);
                  return (
                    <tr key={incident.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 text-sm font-medium text-gray-900">{incident.id}</td>
                      <td className="px-6 py-4 text-sm text-gray-900">{incident.clientName}</td>
                      <td className="px-6 py-4 text-sm text-blue-600">{incident.operationId}</td>
                      <td className="px-6 py-4 text-sm text-gray-900">{incident.type}</td>
                      <td className="px-6 py-4 text-center">
                        <span className={`px-2 py-1 text-xs font-medium rounded-full ${priorityBadge.class}`}>
                          {priorityBadge.label}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-900">{incident.assignedTo}</td>
                      <td className="px-6 py-4 text-center">
                        <span className={`px-2 py-1 text-xs font-medium rounded-full ${statusBadge.class}`}>
                          {statusBadge.label}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-600">
                        {new Date(incident.createdDate).toLocaleDateString('es-GT')}
                      </td>
                      <td className="px-6 py-4 text-center">
                        <button
                          onClick={() => {
                            setSelectedIncident(incident);
                            setShowDetailModal(true);
                          }}
                          className="text-blue-600 hover:text-blue-800"
                        >
                          <i className="ri-eye-line text-lg"></i>
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        {/* Modal Crear Incidencia */}
        {showCreateModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6 border-b border-gray-200 flex items-center justify-between">
                <h3 className="text-xl font-semibold text-gray-900">Nueva Incidencia</h3>
                <button
                  onClick={() => setShowCreateModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <i className="ri-close-line text-2xl"></i>
                </button>
              </div>

              <div className="p-6 space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Cliente *</label>
                    <select className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent">
                      <option value="">Seleccionar cliente</option>
                      <option>Importadora Global S.A.</option>
                      <option>Exportadora del Pacífico</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Operación Relacionada</label>
                    <input
                      type="text"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="OP-2024-XXXX"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Tipo de Incidencia *</label>
                    <select className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent">
                      <option value="">Seleccionar tipo</option>
                      <option>Retraso en Entrega</option>
                      <option>Documentación Incorrecta</option>
                      <option>Daño en Mercancía</option>
                      <option>Cobro Incorrecto</option>
                      <option>Falta de Comunicación</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Prioridad *</label>
                    <select className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent">
                      <option value="">Seleccionar prioridad</option>
                      <option value="critical">Crítica</option>
                      <option value="high">Alta</option>
                      <option value="medium">Media</option>
                      <option value="low">Baja</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Descripción *</label>
                  <textarea
                    rows={4}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Describa detalladamente la incidencia..."
                  ></textarea>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Responsable *</label>
                  <select className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent">
                    <option value="">Asignar responsable</option>
                    <option>Carlos Méndez</option>
                    <option>Ana García</option>
                    <option>Roberto Silva</option>
                    <option>María López</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Evidencia (Opcional)</label>
                  <input
                    type="file"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
              </div>

              <div className="p-6 border-t border-gray-200 flex justify-end gap-3">
                <button
                  onClick={() => setShowCreateModal(false)}
                  className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                >
                  Cancelar
                </button>
                <button className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">
                  Crear Incidencia
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Modal Detalle */}
        {showDetailModal && selectedIncident && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6 border-b border-gray-200 flex items-center justify-between">
                <h3 className="text-xl font-semibold text-gray-900">Detalle de Incidencia</h3>
                <button
                  onClick={() => setShowDetailModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <i className="ri-close-line text-2xl"></i>
                </button>
              </div>

              <div className="p-6 space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">ID</label>
                    <p className="text-gray-900">{selectedIncident.id}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Estado</label>
                    <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusBadge(selectedIncident.status).class}`}>
                      {getStatusBadge(selectedIncident.status).label}
                    </span>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Cliente</label>
                    <p className="text-gray-900">{selectedIncident.clientName}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Operación</label>
                    <p className="text-blue-600">{selectedIncident.operationId}</p>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Tipo</label>
                    <p className="text-gray-900">{selectedIncident.type}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Prioridad</label>
                    <span className={`px-2 py-1 text-xs font-medium rounded-full ${getPriorityBadge(selectedIncident.priority).class}`}>
                      {getPriorityBadge(selectedIncident.priority).label}
                    </span>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Descripción</label>
                  <p className="text-gray-900">{selectedIncident.description}</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Responsable</label>
                  <p className="text-gray-900">{selectedIncident.assignedTo}</p>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Fecha Creación</label>
                    <p className="text-gray-900">{new Date(selectedIncident.createdDate).toLocaleDateString('es-GT')}</p>
                  </div>
                  {selectedIncident.resolvedDate && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Fecha Resolución</label>
                      <p className="text-gray-900">{new Date(selectedIncident.resolvedDate).toLocaleDateString('es-GT')}</p>
                    </div>
                  )}
                </div>

                {selectedIncident.status !== 'resolved' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Actualizar Estado</label>
                    <select className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent">
                      <option value="open">Abierta</option>
                      <option value="in_progress">En Proceso</option>
                      <option value="resolved">Resuelta</option>
                    </select>
                  </div>
                )}
              </div>

              <div className="p-6 border-t border-gray-200 flex justify-end gap-3">
                <button
                  onClick={() => setShowDetailModal(false)}
                  className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                >
                  Cerrar
                </button>
                {selectedIncident.status !== 'resolved' && (
                  <button className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">
                    Actualizar
                  </button>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}
